import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, XCircle, Link2, Send, Unplug, RefreshCw } from "lucide-react";
import { SettingsCard, SectionLabel, Field } from "./ui";
import { Button } from "@/components/ui-kit";

const SDK_SRC = "https://app.z-api.io/sdk.js";

/**
 * Carrega o SDK da Z-API uma única vez.
 *
 * Uma tag por clique deixaria várias cópias do conector na página, cada uma
 * escutando os mesmos eventos — e o modal abriria duplicado. A Promise fica
 * guardada no módulo para que cliques simultâneos esperem o MESMO carregamento.
 */
let sdkPromise = null;
function carregarSdk() {
  if (window.ZAPIConnector) return Promise.resolve(window.ZAPIConnector);
  sdkPromise ??= new Promise((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = SDK_SRC;
    tag.async = true;
    tag.onload = () => (window.ZAPIConnector
      ? resolve(window.ZAPIConnector)
      : reject(new Error("O SDK da Z-API carregou mas não se registrou.")));
    tag.onerror = () => {
      // Sem isto, uma queda de rede envenenaria o cache: toda tentativa
      // seguinte receberia a mesma Promise já rejeitada, e só um F5 resolveria.
      sdkPromise = null;
      reject(new Error("Não foi possível carregar o SDK da Z-API. Verifique a conexão."));
    };
    document.head.appendChild(tag);
  });

  return sdkPromise;
}

/**
 * A Z-API devolve a assinatura vencida como um `error` de texto no status.
 *
 * Vale distinguir de "desconectado": são problemas com soluções opostas. O
 * desconectado se resolve lendo o QR Code aqui; o expirado não — o modal abre
 * mostrando "instância expirada" e nenhum QR aparece, porque o WhatsApp deixa
 * de aceitar a conexão. Sem esta distinção o lojista tentaria reconectar em
 * laço sem entender por que o código nunca vem.
 */
export const assinaturaExpirada = (erro) => /subscribe|expir/i.test(String(erro || ""));

function Status({ conectado, configurado, telefone, erro }) {
  if (!configurado) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
        <XCircle size={13} aria-hidden /> Sem credenciais
      </span>
    );
  }

  if (!conectado && assinaturaExpirada(erro)) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-2.5 py-1 text-xs font-medium text-red-700"
        data-testid="wa-status-expired">
        <XCircle size={13} aria-hidden /> Assinatura expirada
      </span>
    );
  }

  return conectado ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-700"
      data-testid="wa-status-on">
      <CheckCircle2 size={13} aria-hidden /> Conectado{telefone ? ` · ${telefone}` : ""}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-700"
      data-testid="wa-status-off">
      <XCircle size={13} aria-hidden /> Desconectado
    </span>
  );
}

/**
 * WhatsApp da loja — conexão e diagnóstico, sem sair daqui.
 *
 * O que esta tela substitui: abrir o painel da Z-API noutra aba, achar a
 * instância, ler o QR Code por lá e voltar. O SDK da Z-API monta o modal do QR
 * dentro desta página; o token que o autoriza é gerado no nosso servidor, porque
 * o Client-Token dá acesso à conta inteira e nunca pode chegar ao navegador.
 */
export default function WhatsAppTab() {
  const [state, setState] = useState({ loading: true, connected: false, configured: false });
  const [form, setForm] = useState({ zapi_instance_id: "", zapi_instance_token: "", zapi_client_token: "" });
  const [busy, setBusy] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [messages, setMessages] = useState([]);
  const vivo = useRef(true);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/status");
      if (!vivo.current) return;
      setState({ loading: false, ...data });
      setForm((f) => ({ ...f, zapi_instance_id: data.instance_id || "" }));
    } catch {
      if (vivo.current) setState({ loading: false, connected: false, configured: false });
    }
  }, []);

  const carregarMensagens = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/messages");
      if (vivo.current) setMessages(data || []);
    } catch { /* diagnóstico; a falta dele não quebra a tela */ }
  }, []);

  useEffect(() => {
    vivo.current = true;
    carregar(); carregarMensagens();

    return () => { vivo.current = false; };
  }, [carregar, carregarMensagens]);

  const salvar = async () => {
    setBusy(true);
    try {
      await api.post("/admin/whatsapp/credentials", form);
      toast.success("Credenciais salvas");
      // Os tokens não voltam do servidor (são secretos), então limpamos os
      // campos: deixá-los preenchidos sugeriria que aquilo é o que está gravado.
      setForm((f) => ({ ...f, zapi_instance_token: "", zapi_client_token: "" }));
      await carregar();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  /** Abre o modal do QR Code aqui dentro. */
  const conectar = async () => {
    setBusy(true);
    try {
      const ZAPIConnector = await carregarSdk();
      const { data } = await api.post("/admin/whatsapp/connect-token");

      const conectou = await ZAPIConnector.open({
        token: data.token,
        locale: "pt",
        // Sem a aba de migração: ela move uma sessão do WhatsApp Web e é a via
        // mais fácil de derrubar sem querer o WhatsApp que a loja usa no balcão.
        methods: { qr: true, phone: true, migrate: false },
        theme: { mode: "light", light: { accent: "#2283D9" } },
        messages: {
          title: "Conectar o WhatsApp da loja",
          subtitle: "Abra o WhatsApp no celular da loja e aponte a câmera para o código.",
        },
        // Instância vencida abre a tela de "expirada" em vez do QR Code, e o
        // botão de reassinar só aparece se este callback existir. Renovar é
        // no painel da Z-API (é lá que está o pagamento), então levamos para lá
        // em vez de deixar a pessoa presa numa tela sem saída.
        onSubscribe: () => window.open("https://app.z-api.io", "_blank", "noopener"),
      });

      await carregar();
      if (conectou) {
        toast.success("WhatsApp conectado");
        // Sem webhook a mensagem sai, mas a confirmação de entrega nunca volta
        // e tudo fica parado em "enviada". Apontamos na hora da conexão.
        const { data: hook } = await api.post("/admin/whatsapp/webhooks");
        if (!hook.ok) toast.warning(hook.message, { duration: 9000 });
      }
    } catch (e) { toast.error(e.message || apiError(e)); }
    finally { setBusy(false); }
  };

  const desconectar = async () => {
    setBusy(true);
    try { await api.post("/admin/whatsapp/disconnect"); toast.success("WhatsApp desconectado"); await carregar(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const testar = async () => {
    if (!testPhone.trim()) { toast.error("Informe um número para o teste"); return; }
    setBusy(true);
    try {
      await api.post("/admin/whatsapp/test", { phone: testPhone });
      toast.success("Mensagem de teste enviada");
      carregarMensagens();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="settings-whatsapp-tab">
      <SettingsCard
        title="WhatsApp da loja"
        icon={MessageCircle}
        right={<Status conectado={state.connected} configurado={state.configured}
            telefone={state.connected_phone} erro={state.error} />}
      >
        <p className="text-sm text-slate-600 mb-5">
          Com o WhatsApp conectado, o orçamento vai sozinho para o cliente assim que é gerado — com o PDF anexo.
          Sem conexão, o sistema continua abrindo o WhatsApp com a mensagem pronta para alguém enviar.
        </p>

        <SectionLabel>Credenciais da Z-API</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="ID da instância" value={form.zapi_instance_id}
            onChange={(v) => setForm({ ...form, zapi_instance_id: v })} mono testId="wa-instance-id" />
          <Field label="Token da instância" value={form.zapi_instance_token}
            onChange={(v) => setForm({ ...form, zapi_instance_token: v })} mono testId="wa-instance-token"
            placeholder={state.has_instance_token ? "•••••••• (salvo)" : ""} />
          <Field label="Client-Token da conta" value={form.zapi_client_token}
            onChange={(v) => setForm({ ...form, zapi_client_token: v })} mono testId="wa-client-token"
            placeholder={state.has_client_token ? "•••••••• (salvo)" : ""} />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Estão no painel da Z-API, em <b>Instâncias</b>. Os tokens ficam guardados no servidor e nunca voltam para esta tela —
          deixe em branco para manter o que já está salvo.
        </p>

        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100">
          <Button variant="secondary" onClick={salvar} loading={busy} data-testid="wa-save">Salvar credenciais</Button>
          <Button icon={Link2} onClick={conectar} loading={busy} disabled={!state.configured} data-testid="wa-connect">
            {state.connected ? "Reconectar" : "Conectar WhatsApp"}
          </Button>
          <Button variant="ghost" icon={RefreshCw} onClick={carregar} data-testid="wa-refresh">Conferir status</Button>
          <div className="flex-1" />
          {state.connected && (
            <Button variant="dangerSoft" icon={Unplug} onClick={desconectar} loading={busy} data-testid="wa-disconnect">
              Desconectar
            </Button>
          )}
        </div>
        {!state.configured && (
          <p className="text-xs text-amber-700 mt-3">
            Salve o ID e o token da instância para liberar a conexão — é com eles que o QR Code é gerado.
          </p>
        )}
        {/* Ler o QR não resolve assinatura vencida: o WhatsApp deixa de aceitar
            a conexão e o modal abre sem código nenhum. Dizer isso aqui evita a
            tentativa em laço. */}
        {state.configured && !state.connected && assinaturaExpirada(state.error) && (
          <p className="text-xs text-red-700 mt-3" data-testid="wa-expired-hint">
            A assinatura desta instância venceu, e o WhatsApp só volta a aceitar a conexão depois da renovação.
            Renove no painel da Z-API — o QR Code não aparece enquanto isso.
          </p>
        )}
      </SettingsCard>

      {state.connected && (
        <SettingsCard title="Enviar um teste" icon={Send}>
          <p className="text-sm text-slate-600 mb-4">
            Prova a ponta a ponta antes de confiar num orçamento real.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-64">
              <Field label="Número com DDD" value={testPhone} onChange={setTestPhone}
                placeholder="11999998888" mono testId="wa-test-phone" />
            </div>
            <Button icon={Send} onClick={testar} loading={busy} data-testid="wa-test-send">Enviar teste</Button>
          </div>
        </SettingsCard>
      )}

      {messages.length > 0 && (
        <SettingsCard title="Últimas mensagens" icon={MessageCircle}>
          <div className="divide-y divide-slate-100">
            {messages.slice(0, 8).map((m) => (
              <div key={m.id} className="flex items-start justify-between gap-3 py-2.5" data-testid="wa-message-row">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-slate-700">{m.phone}</p>
                  <p className="text-xs text-slate-400 truncate max-w-md">{m.error || m.body}</p>
                </div>
                <span className={`text-xs font-medium shrink-0 ${
                  m.status === "erro" ? "text-red-600"
                    : m.status === "lida" ? "text-emerald-700"
                      : m.status === "entregue" ? "text-emerald-600" : "text-slate-500"}`}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
